import React, { useReducer, ChangeEvent, useState, useMemo, useRef, useCallback } from 'react';
import { Box, MenuItem, TableCell, Tooltip, Typography, useTheme } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';

import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import CustomSelect from '@shared/components/forms/theme-elements/CustomSelect';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import { IconChevronDown } from '@shared/assets/images/svgs';
import { useStyles } from './styles';
import { useStyles as useSharedStyles } from '../../styles';
import {
  AgentDto,
  TranscriberResponseDto,
  useAgentProfileListControllerAgentProfileListForUsersQuery,
  useCompanyControllerGetCompanyQuery,
  useLlmModelListControllerModelListQuery,
} from '@shared/services/apiService/apiService';
import { useEffect } from 'react';
import { useCreateAgent } from '@features/Admin/Agent/hooks/useCreateAgent';
import { useParams } from 'react-router-dom';
import { useAppSelector } from '@shared/store';
import { selectors } from '@shared/store/ducks';
import { AgentProfileRequestDto } from '@features/Admin/Agent/model/types';
import { accentOptions, DEFAULT_NO_ROLE_AGENT_PROFILE, mapLanguageToISO639 } from './constants';
import { formatTranscriberLabel } from '@shared/utils/transcriberDisplay';
import { RolePicker } from './RolePicker';
import { VoicePicker } from './VoicePicker';
import { playVoicePreviewFromUserGesture } from '../playVoicePreview';

interface State {
  agent: string;
  sex: string;
  voice?: string;
  language: string;
  transcriberId: string;
  profile?: AgentProfileRequestDto;
}

type FieldKey =
  | 'agent'
  | 'sex'
  | 'voice'
  | 'language'
  | 'transcriberId'
  | 'profile'
  | 'llmProviderId'
  | 'llmModel';

const FIELD_FOCUS_ORDER: FieldKey[] = [
  'agent',
  'sex',
  'voice',
  'language',
  'transcriberId',
  'llmProviderId',
  'llmModel',
  'profile',
];

enum ActionEnum {
  SET_AGENT = 'SET_AGENT',
  SET_SEX = 'SET_SEX',
  SET_VOICE = 'SET_VOICE',
  SET_LANGUAGE = 'SET_LANGUAGE',
  SET_TRANSCRIBER = 'SET_TRANSCRIBER',
  SET_PROFILE = 'SET_PROFILE',
  RESET = 'RESET',
}

type Action =
  | { type: ActionEnum.SET_AGENT; payload: string }
  | { type: ActionEnum.SET_SEX; payload: string }
  | { type: ActionEnum.SET_VOICE; payload?: string }
  | { type: ActionEnum.SET_LANGUAGE; payload: string }
  | { type: ActionEnum.SET_TRANSCRIBER; payload: string }
  | { type: ActionEnum.SET_PROFILE; payload?: AgentProfileRequestDto }
  | { type: ActionEnum.RESET };

const initialAgentState: State = {
  agent: '',
  sex: '',
  voice: '',
  language: '',
  transcriberId: '',
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionEnum.SET_AGENT:
      return { ...state, agent: action.payload };
    case ActionEnum.SET_SEX:
      return { ...state, sex: action.payload };
    case ActionEnum.SET_VOICE:
      return { ...state, voice: action.payload };
    case ActionEnum.SET_TRANSCRIBER:
      return { ...state, transcriberId: action.payload };
    case ActionEnum.SET_LANGUAGE:
      return { ...state, language: action.payload };
    case ActionEnum.SET_PROFILE:
      return { ...state, profile: action.payload };
    case ActionEnum.RESET:
      return initialAgentState;
    default:
      return state;
  }
};

const CompositeFieldContainer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <Box display="flex" flexDirection="column" gap={1} sx={{ minWidth: 0 }}>
      {children}
    </Box>
  );
};

const FieldErrorSlot: React.FC<{ message?: string; className: string }> = ({
  message,
  className,
}) => (
  <Typography className={className} sx={{ visibility: message ? 'visible' : 'hidden' }}>
    {message || '\u00A0'}
  </Typography>
);

const CustomSelectField: React.FC<{
  control: any;
  labelId: string;
  name: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => void;
  placeholder: string;
  options: { key: string; value: string; label: string; icon?: React.ReactNode }[];
  disabled?: boolean;
  renderValue?: (selected: any) => React.ReactNode;
  className?: string;
  error?: boolean;
}> = ({
  control,
  labelId,
  name,
  value,
  onChange,
  placeholder,
  options,
  disabled,
  renderValue,
  className,
  error = false,
}) => {
  const theme = useTheme();
  const { classes } = useStyles();

  const defaultRenderValue = (value: string) => {
    if (!value) {
      return <Typography color={theme.palette.extendedGrey?.neutral}>{placeholder}</Typography>;
    }
    return <>{value}</>;
  };

  return (
    <CustomSelect
      control={control}
      labelId={labelId}
      name={name}
      onChange={onChange}
      id={`${labelId}-id`}
      disabled={disabled}
      value={value}
      IconComponent={IconChevronDown}
      className={className || classes.select}
      error={error}
      displayEmpty
      renderValue={renderValue ? renderValue : defaultRenderValue}>
      {options.map((option) => (
        <MenuItem key={option.key} value={option.value}>
          <Box className={classes.selectMenuItemContent}>
            {option.icon}
            {!option.icon && option.label}
          </Box>
        </MenuItem>
      ))}
    </CustomSelect>
  );
};

interface ManageAgentProps {
  onClose: () => Promise<void> | void;
  onCreated?: () => Promise<void>;
  agents: AgentDto[];
  agent: AgentDto | null;
  squadId?: string | null;
  transcriberData?: TranscriberResponseDto[];
}

export const ManageAgent: React.FC<ManageAgentProps> = ({
  onClose,
  onCreated,
  agents,
  agent,
  squadId,
  transcriberData,
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    agent
      ? ({
          agent: agent.name,
          sex: agent.voice?.gender,
          language: mapLanguageToISO639((agent.languages?.length && agent?.languages[0]) || ''),
          transcriberId: agent.transcriberId || agent.transcriber?.id || '',
          voice: agent.voice?.id,
          profile: agent.profile || DEFAULT_NO_ROLE_AGENT_PROFILE,
        } as State)
      : initialAgentState,
  );

  const user = useAppSelector(selectors.auth.getUser);
  const companyId = user?.companyId ?? '';
  const { t } = useTranslation();
  const { data: company, isLoading: isCompanyLoading } = useCompanyControllerGetCompanyQuery(
    { id: companyId },
    { skip: !companyId, refetchOnMountOrArgChange: true },
  );
  const voiceData = company?.voices;
  const llmProviders = company?.llmProviders;
  const hasNoLlmProviders = !isCompanyLoading && (llmProviders?.length ?? 0) === 0;
  const noLlmProvidersMessage = t(
    'adminMainPage.agents.fieldValidation.noLlmProvidersAvailable',
  ) as string;

  if (!user?.companyId) {
    return <div>User not found...</div>;
  }

  const { classes } = useStyles();
  const { classes: sharedClasses } = useSharedStyles();

  const { campaignId } = useParams();

  const {
    control,
    isLoading: isCreatingAgent,
    setValue,
    onSubmit,
  } = useCreateAgent(campaignId || '', agents, squadId);

  const { data: agentProfileTypes } = useAgentProfileListControllerAgentProfileListForUsersQuery({
    filter: 'system,custom',
  });

  const agentProfileSelectList = useMemo(
    () => agentProfileTypes?.concat([DEFAULT_NO_ROLE_AGENT_PROFILE]),
    [agentProfileTypes],
  );

  // LLM config local state
  const [selectedProviderId, setSelectedProviderId] = useState<string>('');
  const [providerAllowsCustom, setProviderAllowsCustom] = useState<boolean>(false);
  const [selectedModelId, setSelectedModelId] = useState<string>('');
  const [customModelKey, setCustomModelKey] = useState<string>('');

  // Fetch models only when provider selected and it does NOT allow custom models
  const { data: llmModels } = useLlmModelListControllerModelListQuery(
    { id: selectedProviderId as string },
    { skip: !selectedProviderId || providerAllowsCustom },
  );

  const [isPlayingVoice, setIsPlayingVoice] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [showValidation, setShowValidation] = useState(false);
  const [saveShake, setSaveShake] = useState(false);
  const [voiceResetHint, setVoiceResetHint] = useState(false);
  const fieldAnchorRefs = useRef<Partial<Record<FieldKey, HTMLTableCellElement | null>>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<FieldKey, string>>({
    agent: '',
    sex: '',
    voice: '',
    language: '',
    transcriberId: '',
    profile: '',
    llmProviderId: '',
    llmModel: '',
  });

  // Initialize form with agent data if editing
  useEffect(() => {
    if (agent) {
      dispatch({ type: ActionEnum.SET_AGENT, payload: agent.name });
      dispatch({ type: ActionEnum.SET_SEX, payload: agent.voice?.gender || '' });
      dispatch({ type: ActionEnum.SET_VOICE, payload: agent?.voice?.id || '' });
      dispatch({
        type: ActionEnum.SET_LANGUAGE,
        payload: mapLanguageToISO639((agent.languages?.length && agent?.languages[0]) || ''),
      });
      dispatch({
        type: ActionEnum.SET_PROFILE,
        payload:
          agentProfileSelectList?.find(
            (p) => (p.name as unknown as string) === (agent.profile?.name as unknown as string),
          ) || DEFAULT_NO_ROLE_AGENT_PROFILE,
      });
      dispatch({
        type: ActionEnum.SET_TRANSCRIBER,
        payload: agent?.transcriberId || agent?.transcriber?.id || '',
      });

      // Prefill LLM configuration
      if (llmProviders && agent.llmProviderId) {
        setSelectedProviderId(agent.llmProviderId);
        const provider = llmProviders.find((p) => p.id === agent.llmProviderId);
        const allowsCustom =
          provider?.allowsCustomModels ?? agent.llmProvider?.allowsCustomModels ?? false;
        setProviderAllowsCustom(allowsCustom);
        if (allowsCustom) {
          setCustomModelKey(agent.llmCustomModelKey || '');
          setSelectedModelId('');
        } else {
          setSelectedModelId(agent.llmModelId || '');
          setCustomModelKey('');
        }
      }
    } else {
      dispatch({ type: ActionEnum.RESET });
      if (llmProviders && llmProviders.length > 0) {
        const firstProvider = llmProviders[0];
        setSelectedProviderId(firstProvider.id);
        setProviderAllowsCustom(firstProvider.allowsCustomModels);
        setSelectedModelId('');
        setCustomModelKey('');
      } else {
        setSelectedProviderId('');
        setProviderAllowsCustom(false);
        setSelectedModelId('');
        setCustomModelKey('');
      }
    }
  }, [agent, agentProfileSelectList, llmProviders]);

  useEffect(() => {
    // Edit init effect owns provider selection for existing agents. Running fallback
    // here with a stale empty selectedProviderId can overwrite saved LLM provider.
    if (agent) return;

    if (!llmProviders?.length) {
      if (selectedProviderId) {
        setSelectedProviderId('');
        setProviderAllowsCustom(false);
        setSelectedModelId('');
        setCustomModelKey('');
      }
      return;
    }
    if (selectedProviderId && llmProviders.some((p) => p.id === selectedProviderId)) {
      return;
    }
    const fallback = llmProviders[0];
    setSelectedProviderId(fallback.id);
    setProviderAllowsCustom(fallback.allowsCustomModels);
    setSelectedModelId('');
    setCustomModelKey('');
  }, [llmProviders, selectedProviderId]);

  useEffect(() => {
    if (!agent && !providerAllowsCustom && llmModels && llmModels.length > 0 && !selectedModelId) {
      setSelectedModelId(llmModels[0].id);
    }
  }, [agent, providerAllowsCustom, llmModels, selectedModelId]);

  useEffect(() => {
    if (agent) return;
    dispatch({ type: ActionEnum.SET_PROFILE, payload: DEFAULT_NO_ROLE_AGENT_PROFILE });
  }, [agent]);

  useEffect(() => {
    if (agent || state.transcriberId || !transcriberData?.length) return;
    if (transcriberData.length === 1) {
      dispatch({ type: ActionEnum.SET_TRANSCRIBER, payload: transcriberData[0].id });
    }
  }, [agent, state.transcriberId, transcriberData]);

  useEffect(() => {
    if (agent || state.language) return;
    const defaultLanguage = accentOptions.find((option) => option.key === 'eng');
    if (defaultLanguage) {
      dispatch({ type: ActionEnum.SET_LANGUAGE, payload: defaultLanguage.key });
    }
  }, [agent, state.language]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  const stopAudioPlayback = () => {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    }
  };

  const focusFirstError = useCallback((errors: Record<FieldKey, string>) => {
    const firstKey = FIELD_FOCUS_ORDER.find((key) => errors[key]);
    if (!firstKey) return;

    const cell = fieldAnchorRefs.current[firstKey];
    cell?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

    const focusable = cell?.querySelector<HTMLElement>(
      'input, button, [role="combobox"], .MuiSelect-select',
    );
    focusable?.focus({ preventScroll: true });
  }, []);

  const handleInputChange =
    (type: Action['type']) => (event: ChangeEvent<HTMLInputElement | { value: unknown }>) => {
      const clearFieldError = (field: FieldKey) => {
        if (!showValidation) return;
        setFieldErrors((prev) => ({ ...prev, [field]: '' }));
      };

      switch (type) {
        case ActionEnum.SET_SEX: {
          const hadVoice = Boolean(state.voice);
          dispatch({ type: ActionEnum.SET_SEX, payload: event.target.value as string });
          dispatch({ type: ActionEnum.SET_VOICE, payload: undefined });
          setVoiceResetHint(hadVoice);
          clearFieldError('sex');
          clearFieldError('voice');
          return;
        }

        case ActionEnum.SET_VOICE:
          dispatch({
            type: ActionEnum.SET_VOICE,
            payload: event.target.value as string,
          });
          clearFieldError('voice');
          return;

        case ActionEnum.SET_AGENT:
        case ActionEnum.SET_LANGUAGE:
          dispatch({ type, payload: event.target.value as string });
          clearFieldError(type === ActionEnum.SET_AGENT ? 'agent' : 'language');
          return;

        case ActionEnum.SET_TRANSCRIBER:
          dispatch({ type, payload: event.target.value as string });
          clearFieldError('transcriberId');
          return;

        default:
          // no need to handle RESET
          return;
      }
    };

  const validateRequiredFields = (): boolean => {
    const isProfileSelected = Boolean(state.profile?.id);

    const nextErrors: Record<FieldKey, string> = {
      agent: state.agent?.trim()
        ? ''
        : (t('adminMainPage.agents.fieldValidation.enterHumanName') as string),
      sex: state.sex ? '' : (t('adminMainPage.agents.fieldValidation.selectSex') as string),
      voice:
        state.sex && !state.voice
          ? (t('adminMainPage.agents.fieldValidation.selectVoice') as string)
          : '',
      language: state.language
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectLanguage') as string),
      transcriberId: state.transcriberId
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectTranscriber') as string),
      profile: isProfileSelected
        ? ''
        : (t('adminMainPage.agents.fieldValidation.selectProfile') as string),
      llmProviderId: selectedProviderId
        ? ''
        : hasNoLlmProviders
          ? noLlmProvidersMessage
          : (t('adminMainPage.agents.fieldValidation.selectLlmProvider') as string),
      llmModel: !selectedProviderId
        ? ''
        : providerAllowsCustom && !customModelKey?.trim()
          ? (t('adminMainPage.agents.fieldValidation.enterCustomLlmModel') as string)
          : !providerAllowsCustom && !selectedModelId
            ? (t('adminMainPage.agents.fieldValidation.selectLlmModel') as string)
            : '',
    };

    setFieldErrors(nextErrors);

    const isValid = Object.values(nextErrors).every((value) => !value);
    if (!isValid) {
      window.requestAnimationFrame(() => focusFirstError(nextErrors));
    }

    return isValid;
  };

  const handleSave = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    stopAudioPlayback();
    setShowValidation(true);

    if (!validateRequiredFields()) {
      setSaveShake(false);
      window.requestAnimationFrame(() => {
        setSaveShake(true);
      });
      window.setTimeout(() => setSaveShake(false), 650);
      return;
    }

    setValue('name', state.agent);
    setValue('voiceId', state?.voice);
    setValue('transcriberId', state?.transcriberId); // TODO: to be implemented CLARIFY WITH MAXIM
    setValue('languages', [mapLanguageToISO639(state.language)]);
    setValue('profile', state.profile);
    setValue('profileId', state.profile?.id);

    // Map LLM config to form values according to rules
    setValue('llmProviderId', selectedProviderId);
    if (providerAllowsCustom) {
      // If provider allows custom models -> send only custom key
      setValue('llmCustomModelKey', customModelKey || null);
      setValue('llmModelId', null);
    } else {
      // If provider does not allow custom models -> send model id
      setValue('llmModelId', selectedModelId || null);
      setValue('llmCustomModelKey', null);
    }

    if (agent) {
      setValue('id', agent?.id || '');
    }

    const success = await onSubmit();
    if (!success) {
      return;
    }

    dispatch({ type: ActionEnum.RESET });
    setShowValidation(false);
    setVoiceResetHint(false);
    if (!agent && onCreated) {
      await onCreated();
      return;
    }
    await onClose();
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    stopAudioPlayback();

    onClose();
    dispatch({ type: ActionEnum.RESET });
    setShowValidation(false);
    setVoiceResetHint(false);
  };

  const sexOptions = Array.from(new Set(voiceData?.map((voice) => voice.gender))).map((gender) => {
    const genderLower = (gender || '').toLowerCase();
    return {
      key: gender || '',
      value: gender || '',
      label: (gender ?? '').charAt(0).toUpperCase() + (gender ?? '').slice(1) || '',
      icon:
        genderLower === 'male' ? (
          <MaleIcon className={classes.maleIcon} />
        ) : genderLower === 'female' ? (
          <FemaleIcon className={classes.femaleIcon} />
        ) : null,
    };
  });

  const filteredVoices = useMemo(
    () => voiceData?.filter((voice) => voice.gender === state.sex) || [],
    [voiceData, state.sex],
  );

  const transcriberOptions =
    transcriberData?.map((transcriber) => {
      return {
        key: transcriber.id,
        value: transcriber.id,
        label: formatTranscriberLabel(transcriber),
      };
    }) || [];

  const playVoicePreview = (previewUrl: string) => {
    if (isPlayingVoice || !previewUrl) return;

    stopAudioPlayback();

    const stopUi = () => {
      setIsPlayingVoice(false);
      setCurrentAudio(null);
    };
    const audio = playVoicePreviewFromUserGesture(previewUrl, stopUi);
    if (!audio) return;
    audio.addEventListener('error', stopUi);
    audio.addEventListener('ended', stopUi);

    setIsPlayingVoice(true);
    setCurrentAudio(audio);
  };

  const handleVoiceIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    if (isPlayingVoice && currentAudio) {
      stopAudioPlayback();
      return;
    }

    if (!state.sex || !state.voice) {
      console.warn('Please select a gender and voice first');
      return;
    }

    const selectedVoice = voiceData?.find((voice) => voice.id === state.voice);
    if (!selectedVoice?.previewUrl) {
      console.warn('No preview URL available for this voice');
      return;
    }

    playVoicePreview(selectedVoice.previewUrl);
  };

  return (
    <>
      <TableCell className={sharedClasses.agentIdCell} sx={{ padding: '8px' }} />
      <TableCell className={sharedClasses.startCell} sx={{ padding: '8px' }} />
      <TableCell
        ref={(el) => {
          fieldAnchorRefs.current.agent = el;
        }}
        className={sharedClasses.nameCell}>
        <CustomTextField
          control={control}
          name="name"
          value={state.agent}
          onChange={handleInputChange(ActionEnum.SET_AGENT)}
          id="agent-name"
          type="text"
          placeholder={t('campaignPage.placeholders.humanName')}
          varient="outlined"
          inputProps={{ maxLength: 56 }}
          fullWidth
          required
          error={showValidation && Boolean(fieldErrors.agent)}
          helperText={showValidation ? fieldErrors.agent : ''}
          disabled={isPlayingVoice}
        />
      </TableCell>

      <TableCell
        ref={(el) => {
          fieldAnchorRefs.current.sex = el;
          fieldAnchorRefs.current.voice = el;
        }}
        className={sharedClasses.voiceCell}>
        <CompositeFieldContainer>
          <Box sx={{ minWidth: 0 }}>
            <CustomSelectField
              control={control}
              labelId="sex-type"
              name="sexType"
              value={state.sex}
              onChange={handleInputChange(ActionEnum.SET_SEX)}
              placeholder={t('campaignPage.placeholders.sexType')}
              options={sexOptions}
              disabled={isPlayingVoice}
              className={classes.sexSelect}
              error={showValidation && Boolean(fieldErrors.sex)}
              renderValue={(selected) => {
                const selectedSex = sexOptions?.find((option) => option.key === selected);
                return (
                  <Box className={classes.genderSelectContainer}>
                    {selectedSex?.icon || selectedSex?.label || ''}
                  </Box>
                );
              }}
            />
            {showValidation && fieldErrors.sex ? (
              <FieldErrorSlot className={classes.fieldErrorText} message={fieldErrors.sex} />
            ) : null}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Box className={classes.voiceContainer}>
              {(() => {
                const selectedVoice = voiceData?.find((voice) => voice.id === state.voice);
                const hasPreview = selectedVoice?.previewUrl;
                const canPlay = state.sex && state.voice && hasPreview;
                const isDisabled = !state.sex || !state.voice;

                const iconClasses = [
                  classes.voiceIcon,
                  isPlayingVoice
                    ? classes.voiceIconPlaying
                    : hasPreview
                      ? classes.voiceIconWithPreview
                      : classes.voiceIconNoPreview,
                  isDisabled ? classes.voiceIconDisabled : '',
                  isPlayingVoice ? classes.voiceIconAnimated : '',
                ]
                  .filter(Boolean)
                  .join(' ');

                return (
                  <Tooltip
                    title={
                      isDisabled
                        ? t('campaignPage.placeholders.selectVoiceFirst')
                        : hasPreview
                          ? t('adminMainPage.agents.voicePreviewAvailable')
                          : t('adminMainPage.agents.noVoicePreview')
                    }
                    arrow
                    placement="top">
                    <Box
                      onClick={canPlay ? handleVoiceIconClick : undefined}
                      className={iconClasses}>
                      {hasPreview ? (
                        <VolumeUpIcon className={classes.volumeIcon} />
                      ) : (
                        <VolumeOffIcon className={classes.volumeIcon} />
                      )}
                    </Box>
                  </Tooltip>
                );
              })()}
              <Box className={classes.voiceSelectWrapper}>
                <VoicePicker
                  voices={filteredVoices}
                  selectedVoiceId={state.voice || ''}
                  disabled={!state.sex || isPlayingVoice}
                  error={showValidation && Boolean(fieldErrors.voice)}
                  placeholder={t('campaignPage.placeholders.voiceType')}
                  onChange={(voiceId) => {
                    dispatch({
                      type: ActionEnum.SET_VOICE,
                      payload: voiceId,
                    });
                    setVoiceResetHint(false);
                    if (showValidation) {
                      setFieldErrors((prev) => ({ ...prev, voice: '' }));
                    }
                  }}
                />
              </Box>
            </Box>
            {showValidation && !fieldErrors.sex && fieldErrors.voice ? (
              <FieldErrorSlot className={classes.fieldErrorText} message={fieldErrors.voice} />
            ) : null}
          </Box>
          {!state.sex && !showValidation ? (
            <Typography className={classes.fieldHintText} sx={{ color: 'text.secondary' }}>
              {t('adminMainPage.agents.fieldValidation.selectSexFirst')}
            </Typography>
          ) : null}
          {voiceResetHint ? (
            <Typography className={classes.fieldHintText}>
              {t('adminMainPage.agents.fieldValidation.voiceResetHint')}
            </Typography>
          ) : null}
        </CompositeFieldContainer>
      </TableCell>

      <TableCell
        ref={(el) => {
          fieldAnchorRefs.current.language = el;
        }}
        className={sharedClasses.languageCell}>
        <CustomSelectField
          control={control}
          labelId="language-type"
          name="languageType"
          value={state.language}
          onChange={handleInputChange(ActionEnum.SET_LANGUAGE)}
          placeholder={t('campaignPage.placeholders.languageType')}
          options={accentOptions}
          error={showValidation && Boolean(fieldErrors.language)}
          disabled={isPlayingVoice}
          renderValue={(selected) => {
            const selectedLang = accentOptions?.find((lang) => lang.key === selected);
            return selectedLang?.label || '';
          }}
        />
        <FieldErrorSlot
          className={classes.fieldErrorText}
          message={showValidation ? fieldErrors.language : ''}
        />
      </TableCell>

      <TableCell
        ref={(el) => {
          fieldAnchorRefs.current.transcriberId = el;
        }}
        className={sharedClasses.transcriberCell}>
        <CustomSelectField
          control={control}
          labelId="transcriber-provider"
          name="transcriberId"
          value={state.transcriberId}
          onChange={handleInputChange(ActionEnum.SET_TRANSCRIBER)}
          placeholder={t('campaignPage.placeholders.transcriberType')}
          options={transcriberOptions}
          error={showValidation && Boolean(fieldErrors.transcriberId)}
          disabled={isPlayingVoice}
          renderValue={(selected) => {
            const selectedTranscriber = transcriberOptions?.find(
              (transcriber) => transcriber.key === selected,
            );
            return selectedTranscriber?.label || '';
          }}
        />
        <FieldErrorSlot
          className={classes.fieldErrorText}
          message={showValidation ? fieldErrors.transcriberId : ''}
        />
      </TableCell>

      <TableCell
        ref={(el) => {
          fieldAnchorRefs.current.llmProviderId = el;
          fieldAnchorRefs.current.llmModel = el;
        }}
        className={sharedClasses.llmCell}>
        <CompositeFieldContainer>
          <Tooltip
            title={hasNoLlmProviders ? noLlmProvidersMessage : ''}
            arrow
            placement="top"
            disableHoverListener={!hasNoLlmProviders}>
            <Box component="span" sx={{ display: 'block', width: '100%' }}>
              <CustomSelectField
                control={control}
                labelId="llm-provider"
                name="llmProviderId"
                value={selectedProviderId}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value as string;
                  setSelectedProviderId(value);
                  const provider = llmProviders?.find((p) => p.id === value);
                  const allows = provider?.allowsCustomModels ?? false;
                  setProviderAllowsCustom(allows);
                  // Reset incompatible fields when provider changes
                  setCustomModelKey('');
                  setSelectedModelId('');
                  if (showValidation) {
                    setFieldErrors((prev) => ({ ...prev, llmProviderId: '', llmModel: '' }));
                  }
                }}
                placeholder={
                  hasNoLlmProviders
                    ? (t('adminMainPage.agents.noneAvailable') as string)
                    : (t('adminMainPage.agents.llmProvider') as string)
                }
                options={
                  llmProviders?.map((p) => ({ key: p.id, value: p.id, label: p.title })) || []
                }
                error={showValidation && Boolean(fieldErrors.llmProviderId)}
                disabled={isPlayingVoice || hasNoLlmProviders}
                renderValue={(selected) => {
                  if (hasNoLlmProviders) {
                    const emptyLabel = t('adminMainPage.agents.noneAvailable') as string;
                    return (
                      <Tooltip title={noLlmProvidersMessage} arrow placement="top">
                        <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {emptyLabel}
                        </Box>
                      </Tooltip>
                    );
                  }
                  const selectedProvider = llmProviders?.find((p) => p.id === selected);
                  const label =
                    selectedProvider?.title ||
                    agent?.llmProvider?.title ||
                    (t('adminMainPage.agents.llmProvider') as string);
                  return (
                    <Tooltip title={label} arrow placement="top">
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {label}
                      </Box>
                    </Tooltip>
                  );
                }}
              />
            </Box>
          </Tooltip>
          {hasNoLlmProviders ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, px: 0.5 }}>
              {noLlmProvidersMessage}
            </Typography>
          ) : null}

          {selectedProviderId &&
            (providerAllowsCustom ? (
              <>
                <CustomTextField
                  control={control}
                  name="llmCustomModelKey"
                  value={customModelKey}
                  onChange={(e: any) => {
                    setCustomModelKey(e.target.value);
                    if (showValidation) {
                      setFieldErrors((prev) => ({ ...prev, llmModel: '' }));
                    }
                  }}
                  id="llm-custom-model-key"
                  type="text"
                  placeholder={t('adminMainPage.agents.llmCustomModelKey')}
                  varient="outlined"
                  fullWidth
                  required
                  error={showValidation && Boolean(fieldErrors.llmModel)}
                  disabled={isPlayingVoice}
                />
                <FieldErrorSlot
                  className={classes.fieldErrorText}
                  message={showValidation ? fieldErrors.llmModel : ''}
                />
              </>
            ) : (
              <CustomSelectField
                control={control}
                labelId="llm-model"
                name="llmModelId"
                value={selectedModelId}
                onChange={(e) => {
                  setSelectedModelId((e.target as HTMLInputElement).value as string);
                  if (showValidation) {
                    setFieldErrors((prev) => ({ ...prev, llmModel: '' }));
                  }
                }}
                placeholder={t('adminMainPage.agents.llmModel')}
                options={llmModels?.map((m) => ({ key: m.id, value: m.id, label: m.title })) || []}
                error={showValidation && Boolean(fieldErrors.llmModel)}
                disabled={isPlayingVoice}
                renderValue={(selected) => {
                  const selectedModel = llmModels?.find((m) => m.id === selected);
                  const label =
                    selectedModel?.title || (t('adminMainPage.agents.llmModel') as string);
                  return (
                    <Tooltip title={label} arrow placement="top">
                      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {label}
                      </Box>
                    </Tooltip>
                  );
                }}
              />
            ))}
          <FieldErrorSlot
            className={classes.fieldErrorText}
            message={showValidation ? fieldErrors.llmProviderId : ''}
          />
          {!providerAllowsCustom ? (
            <FieldErrorSlot
              className={classes.fieldErrorText}
              message={showValidation ? fieldErrors.llmModel : ''}
            />
          ) : null}
        </CompositeFieldContainer>
      </TableCell>

      <TableCell
        ref={(el) => {
          fieldAnchorRefs.current.profile = el;
        }}
        className={sharedClasses.profileCell}>
        <RolePicker
          profiles={agentProfileSelectList}
          selected={
            agentProfileSelectList?.find((profile) => profile.id === state?.profile?.id) ?? null
          }
          disabled={isPlayingVoice}
          error={showValidation && Boolean(fieldErrors.profile)}
          onChange={(profile) => {
            dispatch({
              type: ActionEnum.SET_PROFILE,
              payload: profile as AgentProfileRequestDto,
            });
            if (showValidation) {
              setFieldErrors((prev) => ({ ...prev, profile: '' }));
            }
          }}
        />
        <FieldErrorSlot
          className={classes.fieldErrorText}
          message={showValidation ? fieldErrors.profile : ''}
        />
      </TableCell>

      <TableCell className={sharedClasses.actionsCell}>
        <Box
          display="flex"
          flexDirection={'column'}
          justifyContent="flex-end"
          alignItems="center"
          gap={1}>
          <Tooltip
            title={
              showValidation && Object.values(fieldErrors).some(Boolean)
                ? (t('adminMainPage.agents.fieldValidation.fixErrorsBeforeSave') as string)
                : (t('common.save') as string)
            }
            arrow
            placement="left">
            <button
              className={classNames(classes.save_button, {
                [classes.save_button_shake]: saveShake,
              })}
              disabled={isCreatingAgent}
              onClick={handleSave}
              type="button"
              aria-label={t('common.save') as string}>
              <CheckIcon htmlColor="white" style={{ width: 16 }} />
            </button>
          </Tooltip>

          <button className={classes.cancel_button} color="secondary" onClick={handleCancel}>
            <CloseIcon htmlColor="#7C8FAC" style={{ width: 16 }} />
          </button>
        </Box>
      </TableCell>
    </>
  );
};
